import { memo } from 'react'
import { EVENT_COUNT_CLASSES } from '@/components/calendar/event-blocks'
import { cn } from '@/lib/utils'
import { itemHandlers, type PortfolioItemActions } from './item-actions'
import {
  MARKER_INSET,
  MARKER_PITCH,
  MARKER_SIZE,
  type GridDensity,
  type PortfolioDayMarker,
} from './layout'

interface PortfolioMarkerViewProps {
  marker: PortfolioDayMarker
  propertyId: string
  actions: PortfolioItemActions
  density: GridDensity
  /** The row's marker strip, from the row's top. */
  top: number
  /** Whether it takes the Tab key: only while it is in view. */
  focusable?: boolean
}

/**
 * One marker at a row's foot: a cleaning, a deposit, supply or review event,
 * or "+N" for a day with more than its cell holds (CAP-1417).
 *
 * A 14px square in the property tab's own colours for the same thing, with its
 * icon; the words are its accessible name, and the grid's tooltip on hover and
 * keyboard focus. A button (CAP-1418): it opens the sheet the property tab
 * opens for the same thing, and "+N" opens the day's list.
 */
export const PortfolioMarkerView = memo(function PortfolioMarkerView({
  marker,
  propertyId,
  actions,
  density,
  top,
  focusable = true,
}: PortfolioMarkerViewProps) {
  const style = {
    left: marker.day * density.dayWidth + MARKER_INSET + marker.slot * MARKER_PITCH,
    top,
    width: MARKER_SIZE,
    height: MARKER_SIZE,
  }
  const base =
    'absolute z-[2] flex cursor-pointer items-center justify-center rounded border p-0 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-1 focus-visible:ring-offset-surface'
  const handlers = {
    ...itemHandlers(actions, propertyId, marker),
    tabIndex: focusable ? undefined : -1,
  }
  if (marker.kind === 'more') {
    return (
      <button
        type="button"
        data-testid="portfolio-more-marker"
        {...handlers}
        aria-label={marker.title}
        className={cn(base, 'text-[8px] font-bold leading-none tabular-nums', EVENT_COUNT_CLASSES)}
        style={style}
      >
        +{marker.count}
      </button>
    )
  }
  const { Icon } = marker
  return (
    <button
      type="button"
      data-testid={marker.testId}
      {...marker.data}
      {...handlers}
      aria-label={marker.title}
      className={cn(base, marker.className)}
      style={style}
    >
      <Icon
        className="h-[9px] w-[9px] shrink-0"
        strokeWidth={marker.strokeWidth}
        aria-hidden="true"
      />
    </button>
  )
})
